import { Link } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import portrait1 from '@/assets/imgs/landing/portrait.webp';
import portrait2 from '@/assets/imgs/landing/portrait2.webp';
import portrait3 from '@/assets/imgs/landing/portrait3.webp';
import portrait4 from '@/assets/imgs/landing/portrait4.webp';
import postScreenshot from '@/assets/imgs/landing/post_screenshot.webp';
import idolCard1 from '@/assets/imgs/landing/idol-card1.png';
import idolCard2 from '@/assets/imgs/landing/idol-card2.png';
import idolCard3 from '@/assets/imgs/landing/idol-card3.png';
import idolCard4 from '@/assets/imgs/landing/idol-card4.png';
import idolCard5 from '@/assets/imgs/landing/idol-card5.png';
import idolCard6 from '@/assets/imgs/landing/idol-card6.png';
import idolCard7 from '@/assets/imgs/landing/idol-card7.png';
import idolCard8 from '@/assets/imgs/landing/idol-card8.png';

const ChatMsg = ({ type, text }) => (
  <div className={`d-flex align-items-end gap-2 mb-2${type === 'user' ? ' flex-row-reverse' : ''}`}>
    {type === 'idol' && (
      <img
        src={portrait1}
        className="rounded-circle flex-shrink-0"
        style={{ width: 32, height: 32, objectFit: 'cover', objectPosition: 'top' }}
        alt=""
      />
    )}
    <p className={`rounded-4 px-3 py-2 mb-0 small ${type === 'user' ? 'bg-primary bg-opacity-25' : 'bg-light'}`}>
      {text}
    </p>
  </div>
);

const ChatBox = ({ messages, viewFullChatLabel, onScrollToTop }) => (
  <div className="position-relative rounded-4 p-4 pb-5 overflow-hidden bg-white shadow-sm">
    {messages.map((m, i) => <ChatMsg key={i} {...m} />)}
    <div className="chat-lock-overlay position-absolute bottom-0 start-0 end-0 d-flex align-items-end justify-content-center pb-3">
      <Link to="/sign-up">
        <button className="btn rounded-pill btn-primary fw-bold" onClick={onScrollToTop}>
          <FontAwesomeIcon icon={['fas', 'hand-point-right']} className="me-1" />
          {viewFullChatLabel}
        </button>
      </Link>
    </div>
  </div>
);

export default function LandingSections({ onScrollToTop }) {
  const { t } = useTranslation();

  const chatMessages = [
    { type: 'user', text: t('landing_chat_msg_1_user') },
    { type: 'idol', text: t('landing_chat_msg_1_idol') },
    { type: 'user', text: t('landing_chat_msg_2_user') },
    { type: 'idol', text: t('landing_chat_msg_2_idol') },
    { type: 'user', text: t('landing_chat_msg_3_user') },
    { type: 'idol', text: t('landing_chat_msg_3_idol') },
  ];

  return (
    <div className="landing-sections">

      {/* 創造有真實個性的 AI 偶像 */}
      <section className="mt-5 mb-section container-lg">
        <Row className="align-items-center g-3 g-lg-5">
          <Col lg={6}>
            <div className="landing-header mb-4">
              <p className="text-primary fw-bold mb-1 fs-20">{t('landing_no_limits')}</p>
              <p className="fs-48 mb-2">{t('landing_create_idol_title')}</p>
              <p className="text-gray fs-24">{t('landing_create_idol_desc')}</p>
            </div>
            <p className="text-gray">{t('landing_members_only')}</p>
            <Link to="/sign-up">
              <Button className="btn-primary rounded-pill fw-bold" onClick={onScrollToTop}>
                <FontAwesomeIcon icon={['fas', 'lock-open']} className="me-2" />
                {t('landing_unlock_free')}
              </Button>
            </Link>
          </Col>
          <Col lg={6}>
            <div className="d-flex align-items-start gap-2">
              <div className="d-flex flex-column align-items-end gap-2">
                <img src={portrait1} className="rounded-5 object-fit-cover"
                  style={{ aspectRatio: '1/1', objectPosition: 'top', width: '90%' }} alt="" />
                <img src={portrait3} className="rounded-5 object-fit-cover"
                  style={{ aspectRatio: '1/1', objectPosition: 'top', width: '85%' }} alt="" />
              </div>
              <div className="d-flex flex-column align-items-start gap-2 mt-4 ms-auto">
                <img src={portrait4} className="rounded-5 object-fit-cover"
                  style={{ aspectRatio: '1/1', objectPosition: 'top', width: '85%' }} alt="" />
                <img src={portrait2} className="rounded-5 object-fit-cover"
                  style={{ aspectRatio: '1/1', objectPosition: 'top', width: '90%' }} alt="" />
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* 看看裡面正在發生什麼 */}
      <section className="mb-section container-lg">
        <Row className="align-items-center g-3 g-lg-5">
          <Col lg={6} className="order-2 order-lg-1">
            <ChatBox
              messages={chatMessages}
              viewFullChatLabel={t('landing_view_full_chat')}
              onScrollToTop={onScrollToTop}
            />
          </Col>
          <Col lg={6} className="order-1 order-lg-2">
            <div className="landing-header">
              <p className="fs-48 mb-2">{t('landing_see_inside_title')}</p>
              <p className="text-gray fs-24 mb-2">{t('landing_see_inside_desc')}</p>
            </div>
          </Col>
        </Row>
      </section>

      {/* 不只是聊天，而是可以互動的偶像 */}
      <section className="mb-section container-lg">
        <Row className="align-items-center g-3 g-lg-5">
          <Col lg={6}>
            <div className="landing-header">
              <p className="fs-48 mb-2">{t('landing_more_than_chat_title')}</p>
              <p className="text-gray fs-24 mb-2">{t('landing_more_than_chat_desc1')}</p>
              <p className="text-gray fs-24">{t('landing_more_than_chat_desc2')}</p>
            </div>
          </Col>
          <Col lg={6}>
            <img src={postScreenshot} alt="Post preview" className="img-fluid rounded-4 shadow-sm w-100" />
          </Col>
        </Row>
      </section>

      {/* 讓你的想像真正成為現實 */}
      <section className="mb-5 container-lg">
        <div className="landing-header text-center mb-section">
          <p className="fs-48 mb-2">{t('landing_imagination_title')}</p>
          <p className="text-gray fs-24 mb-2">{t('landing_imagination_desc1')}</p>
          <p className="text-gray fs-24">{t('landing_imagination_desc2')}</p>
        </div>
        <h4 className="mb-4">{t('landing_bring_idol_alive')}</h4>
        <Row className="g-3 g-lg-5">
          {[
            { text: t('landing_feature_1') },
            { text: t('landing_feature_2') },
            { text: t('landing_feature_3'), badge: t('coming_soon') },
          ].map(({ text, badge }, i) => (
            <Col key={i} md={4}>
              <div className="d-flex flex-column">
                <span className="display-6 fw-bold text-primary text-opacity-50 lh-1 border-bottom border-primary border-4 pb-2 mb-2">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="fs-18">
                  {text} {badge && <span className="badge bg-primary rounded-pill fw-normal ms-2">{badge}</span>}
                </span>
              </div>
            </Col>
          ))}
        </Row>
      </section>

      {/* Idol cards — 滿版 */}
      <section className="mb-section container-lg">
        <div>
          <div className="d-flex gap-1 mb-1 pe-0 pe-lg-5">
            {[idolCard1, idolCard2, idolCard3, idolCard4].map((src, i) => (
              <div key={i} className={i === 3 ? 'd-none d-lg-block' : ''} style={{ flex: 1, minWidth: 0 }}>
                <img src={src} alt="" className="w-100 rounded-3 shadow-sm" style={{ display: 'block' }} />
              </div>
            ))}
          </div>
          <div className="d-flex gap-1 ps-0 ps-lg-5">
            {[idolCard5, idolCard6, idolCard7, idolCard8].map((src, i) => (
              <div key={i} className={i === 3 ? 'd-none d-lg-block' : ''} style={{ flex: 1, minWidth: 0 }}>
                <img src={src} alt="" className="w-100 rounded-3 shadow-sm" style={{ display: 'block' }} />
              </div>
            ))}
          </div>
        </div>
      </section>

       {/* CTA Banner */}
       <section className="bg-primary py-5 mb-5">
        <Container fluid="lg" className="text-center text-white py-3">
          <h2 className="fw-bold mb-3">{t('landing_cta_title')}</h2>
          <p className="fs-20 mb-4 text-white">{t('landing_cta_desc')}</p>
          <Link to="/sign-up">
            <Button className="btn btn-outline-primaryF rounded-pill fw-bold border border-white border-3 px-5" onClick={onScrollToTop}>
              <FontAwesomeIcon icon={['fas', 'lock-open']} className="me-2" />
              {t('landing_unlock_free')}
            </Button>
          </Link>
        </Container>
      </section>

      {/* 進入一個充滿個性的世界 */}
      <section className="landing-features mb-section container-lg">
        <Row className="g-3 g-lg-5">
          <Col lg={6}>
            <h4 className="mb-4">{t('landing_world_with_personality')}</h4>
            <div className="d-inline-flex flex-column gap-3">
              {[
                { icon: 'universal-access', text: t('landing_discover_idols') },
                { icon: 'comment-dots',     text: t('landing_evolving_chat') },
                { icon: 'heart',            text: t('landing_find_connection') },
              ].map(({ icon, text }) => (
                <div key={text} className="d-flex align-items-center">
                  <div className="landing-icon-circle rounded-circle d-flex align-items-center justify-content-center flex-shrink-0">
                    <FontAwesomeIcon icon={['fas', icon]} className="text-primary fs-5" />
                  </div>
                  <div className="flex-grow-1 bg-white rounded-pill shadow-sm landing-pill">
                    <span className="fw-semibold">{text}</span>
                  </div>
                </div>
              ))}
            </div>
          </Col>

          <Col lg={6}>
            <h4 className="mb-4">{t('landing_world_operating')}</h4>
            <div className="d-inline-flex flex-column gap-3">
              {[
                { icon: 'fire',     text: t('landing_trending_idols') },
                { icon: 'comments', text: t('landing_live_conversations') },
                { icon: 'bolt',     text: t('landing_new_chars_daily') },
              ].map(({ icon, text }) => (
                <div key={text} className="d-flex align-items-center">
                  <div className="landing-icon-circle rounded-circle d-flex align-items-center justify-content-center flex-shrink-0">
                    <FontAwesomeIcon icon={['fas', icon]} className="text-primary fs-5" />
                  </div>
                  <div className="flex-grow-1 bg-white rounded-pill shadow-sm landing-pill">
                    <span className="fw-semibold">{text}</span>
                  </div>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </section>

    </div>
  );
}
